import React, { useCallback, useContext, useMemo, useState } from 'react';
import { style } from 'typestyle';
import { useTranslation } from 'react-i18next';
import MagnifyIcon from 'mdi-react/MagnifyIcon';
import PlusIcon from 'mdi-react/PlusIcon';
import WrenchOutlineIcon from 'mdi-react/WrenchOutlineIcon';
import FormatListBulletedIcon from 'mdi-react/FormatListBulletedIcon';
import ViewGridOutlineIcon from 'mdi-react/ViewGridOutlineIcon';
import FilterRemoveOutlineIcon from 'mdi-react/FilterRemoveOutlineIcon';
import FacilityCard from '../cards/facility-card.component';
import FacilityIdentity from '../cards/facility-identity.component';
import CreateFacilityModal
  from '../modals/create-facility-modal.component';
import {
  createFacility,
  updateFacility,
  deleteFacilityById,
} from '../services/facilities.service';
import type Facility from '../models/facility';
import stringIsNullOrWhitespace
  from '../../../core/utils/validation-utils';
import notify
  from '../../../core/components/notify/notify.component';
import { Center }
  from '../../../core/components/center/center.component';
import ActivityIndicator
  from '../../../core/components/activity-indicator/activity-indicator.component';
import StyleConstants from '../../../core/styles.constants';
import { IsMobileContext }
  from '../../../core/context/is-mobile';
import { useKeycloak }
  from '../../authentication/keycloak/KeycloakProvider';
import * as linkedStyles from './linked-facilities.styles';
import * as listStyles from '../../../core/admin-list.styles';
import ListPager from '../../../core/components/list-pager/list-pager.component';
import * as surfaceStyles from '../../../core/admin-surface.styles';
import { useSessionState } from '../../../core/hooks/session-state.hook';
import type Office from '../../offices/models/office';
import { FacilityTypeEnum }
  from '../../facility-types/enums/facility-type';
import getIconFromFacilityTypeEnum
  from '../../facility-types/utils/facility-type-enum-to-icon-mapper';
import {
  FACILITY_TYPE_ORDER,
  accentForFacilityType,
  facilityTypeLabelKey,
} from '../facility-type-appearance';

const ALL_OFFICES_VALUE = '__all__';
// Facilities nobody can book because they hang off no office — worth being able to isolate.
const NOT_LINKED_VALUE = '__not_linked__';
const ALL_TYPES_VALUE = '__all_types__';
const ITEMS_PER_PAGE = 10;

/** Session-storage keys for this tab's toolbar. Namespaced by tab so the three do not share. */
const FILTER_KEYS = {
  search: 'my-office.facilities.search',
  office: 'my-office.facilities.office',
  type: 'my-office.facilities.type',
  sort: 'my-office.facilities.sort',
  view: 'my-office.facilities.view',
};

type FacilitySort = 'name' | 'type' | 'unlinked';
type FacilityView = 'list' | 'grid';

const SORT_OPTIONS: { value: FacilitySort; labelKey: string }[] = [
  { value: 'name', labelKey: 'sort-name' },
  { value: 'type', labelKey: 'sort-type' },
  { value: 'unlinked', labelKey: 'sort-unlinked-first' },
];

interface Props {
  readonly allFacilities: Facility[] | undefined;
  readonly allOffices?: Office[];
  readonly initialized: boolean;
  readonly readonly: boolean;
}

const OfficeFacilitiesGroup = (
  {
    allFacilities,
    allOffices,
    initialized,
    readonly: isReadonly,
  }: Props,
): React.JSX.Element => {
  const isMobile = useContext(IsMobileContext);
  const { t } = useTranslation();
  const [showCreateFacilityModal, setShowCreateFacilityModal] = (
    useState(false)
  );
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(0);
  const keycloak = useKeycloak();
  const [facilities, setFacilities] = useState(allFacilities);
  // Held in session storage so a reload does not throw away the filters the admin set up.
  // Page is deliberately not persisted: the list it indexes into can change between visits.
  const [searchQuery, setSearchQuery] = useSessionState(FILTER_KEYS.search, '');
  const [selectedOffice, setSelectedOffice] = useSessionState(
    FILTER_KEYS.office,
    ALL_OFFICES_VALUE,
  );
  const [selectedType, setSelectedType] = useSessionState(
    FILTER_KEYS.type,
    ALL_TYPES_VALUE,
  );
  const [sortOrder, setSortOrder] = useSessionState<FacilitySort>(FILTER_KEYS.sort, 'name');
  const [view, setView] = useSessionState<FacilityView>(FILTER_KEYS.view, 'list');

  // Sort order and the list/grid switch are display preferences rather than filters: they
  // hide nothing, so clearing does not touch them.
  const hasActiveFilters = searchQuery.trim() !== ''
    || selectedOffice !== ALL_OFFICES_VALUE
    || selectedType !== ALL_TYPES_VALUE;

  const clearFilters = useCallback((): void => {
    setSearchQuery('');
    setSelectedOffice(ALL_OFFICES_VALUE);
    setSelectedType(ALL_TYPES_VALUE);
    setPage(0);
  }, [setSearchQuery, setSelectedOffice, setSelectedType]);

  const sortedFacilities = useMemo(
    () => [...(facilities ?? [])].sort(
      (a, b) => (
        a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1
      ),
    ),
    [facilities],
  );

  const filteredFacilities = useMemo(() => {
    let result = sortedFacilities;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(
        (f) => f.name.toLowerCase().includes(q),
      );
    }
    if (selectedOffice === NOT_LINKED_VALUE) {
      result = result.filter((f) => f.officeIds.length === 0);
    } else if (selectedOffice !== ALL_OFFICES_VALUE) {
      result = result.filter(
        (f) => f.officeIds.includes(selectedOffice),
      );
    }
    if (selectedType !== ALL_TYPES_VALUE) {
      result = result.filter(
        (f) => f.type === Number(selectedType),
      );
    }

    // `sortedFacilities` is already name-ordered, so the other orders only need their own key
    // with the name as the tie-breaker. Types sort by their translated label rather than by the
    // backend enum, whose numeric order ("other" first) means nothing to the reader.
    if (sortOrder === 'type') {
      const typeLabel = (facility: Facility): string => t(facilityTypeLabelKey(facility.type)) ?? '';
      result = [...result].sort((a, b) => (
        typeLabel(a).localeCompare(typeLabel(b))
        || (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1)
      ));
    } else if (sortOrder === 'unlinked') {
      result = [...result].sort((a, b) => (
        a.officeIds.length - b.officeIds.length
        || (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1)
      ));
    }

    return result;
  }, [
    sortedFacilities,
    searchQuery,
    selectedOffice,
    selectedType,
    sortOrder,
    t,
  ]);

  // Deleting the last item on the last page shrinks the list under the current page; without
  // clamping the panel renders an empty slice and the pager hides itself, stranding the reader.
  const lastPage = Math.max(0, Math.ceil(filteredFacilities.length / ITEMS_PER_PAGE) - 1);
  const currentPage = Math.min(page, lastPage);

  const paginatedFacilities = filteredFacilities.slice(
    currentPage * ITEMS_PER_PAGE,
    (currentPage + 1) * ITEMS_PER_PAGE,
  );

  // How many facilities exist that nobody can reach, called out under the title.
  const unlinkedCount = sortedFacilities.filter(
    (facility) => facility.officeIds.length === 0,
  ).length;

  const handleShowCreate = (): void => {
    setShowCreateFacilityModal(true);
  };
  const handleClose = (): void => {
    setShowCreateFacilityModal(false);
  };

  const facilityIsValid = (
    facilityToValidate: Facility,
  ): boolean => {
    if (stringIsNullOrWhitespace(facilityToValidate.name)) {
      notify(t('name-empty-message'), false);
      return false;
    }
    if (
      sortedFacilities.some(
        (existing) => (
          existing.name.toLowerCase()
            === facilityToValidate.name.toLowerCase()
          && existing.id !== facilityToValidate.id
        ),
      )
    ) {
      notify(t('name-already-used'), false);
      return false;
    }
    return true;
  };

  const shouldCreateFacility = async (
    facilityToCreate: Facility,
  ): Promise<void> => {
    const result = await createFacility(
      facilityToCreate, keycloak,
    );
    if (result != null) {
      facilities?.push(result);
      setFacilities(facilities ?? []);
      handleClose();
    } else if (!result) {
      notify(t('create-facility-error-message'), false);
    }
  };

  const shouldUpdateFacility = async (
    originalFacility: Facility,
    updatedFacility: Facility,
  ): Promise<void> => {
    const result = await updateFacility(
      originalFacility, updatedFacility, keycloak,
    );
    if (result != null) {
      const filtered = facilities?.filter(
        (f) => f.id !== result.id,
      );
      filtered?.push(result);
      setFacilities(filtered);
      handleClose();
    } else if (!result) {
      notify(t('edit-facility-error-message'), false);
    }
  };

  const shouldDeleteFacility = async (
    facilitytoDelete: Facility,
  ): Promise<void> => {
    if (facilitytoDelete.id != null) {
      await deleteFacilityById(facilitytoDelete.id, keycloak);
      const remaining = facilities?.filter(
        (f) => f.id !== facilitytoDelete.id,
      );
      setFacilities(remaining);
      handleClose();
    } else {
      notify(t('delete-facility-error-message'), false);
    }
  };

  const handleSearchChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  const handleOfficeFilterChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ): void => {
    setSelectedOffice(e.target.value);
    setPage(0);
  };

  const handleTypeFilterChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ): void => {
    setSelectedType(e.target.value);
    setPage(0);
  };

  if (isLoading && initialized) {
    setIsLoading(false);
    setFacilities(allFacilities);
  }

  const facilityTypeOptions = Object.keys(FacilityTypeEnum)
    .filter((key) => Number.isNaN(Number(key)))
    .map((key) => ({
      label: t(
        `enums.facility-type-enum.${key}`,
      ),
      value: String(
        FacilityTypeEnum[key as keyof typeof FacilityTypeEnum],
      ),
    }));

  const renderSectionHead = (): React.JSX.Element => (
    <div className={style(surfaceStyles.cardHead(isMobile))}>
      <div className={style(listStyles.panelHeadLeft)}>
        <div className={style(listStyles.sectionTile)}>
          <WrenchOutlineIcon size={20} />
        </div>
        <div>
          <h2 className={style(listStyles.panelTitle)}>
            {t('facilities')}
            {!isLoading && (
              <span className={style(listStyles.countPill)}>
                {sortedFacilities.length}
              </span>
            )}
          </h2>
          {!isLoading && unlinkedCount > 0 && (
            <span className={style(listStyles.warningLine)}>
              {(t('facilities-not-linked-warning') ?? '').replace('[x]', unlinkedCount.toString())}
            </span>
          )}
        </div>
      </div>
      <button
        type="button"
        className={style(surfaceStyles.primaryButton)}
        onClick={handleShowCreate}
        disabled={isLoading}
      >
        <PlusIcon size={16} />
        {t('create-new-facility')}
      </button>
    </div>
  );

  const renderToolbar = (): React.JSX.Element | null => {
    if (isLoading || sortedFacilities.length === 0) return null;
    return (
      <div className={style(listStyles.toolbar(isMobile))}>
        <div className={style(surfaceStyles.searchWrap(isMobile))}>
          <span className={style(surfaceStyles.searchIcon)}>
            <MagnifyIcon size={16} />
          </span>
          <input
            type="text"
            className={style(surfaceStyles.searchInput)}
            placeholder={String(t('search-by-name'))}
            aria-label={String(t('search-by-name'))}
            value={searchQuery}
            onChange={handleSearchChange}
          />
        </div>
        {allOffices && (
          <select
            className={style(listStyles.filterSelect(isMobile))}
            value={selectedOffice}
            aria-label={String(t('all-offices'))}
            onChange={handleOfficeFilterChange}
          >
            <option value={ALL_OFFICES_VALUE}>{t('all-offices')}</option>
            <option value={NOT_LINKED_VALUE}>{t('not-linked')}</option>
            {[...allOffices]
              .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1))
              .map((office) => (
                <option key={office.id} value={office.id ?? ''}>
                  {office.name}
                </option>
              ))}
          </select>
        )}
        <select
          className={style(listStyles.filterSelect(isMobile))}
          value={selectedType}
          aria-label={String(t('all-types'))}
          onChange={handleTypeFilterChange}
        >
          <option value={ALL_TYPES_VALUE}>{t('all-types')}</option>
          {facilityTypeOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        {/* Sort and the view switch sit at the far end of the toolbar. */}
        <select
          className={style(listStyles.sortSelect(isMobile))}
          value={sortOrder}
          aria-label={String(t('sort-by'))}
          onChange={(e): void => {
            const next = SORT_OPTIONS.find((option) => option.value === e.target.value);
            if (next != null) {
              setSortOrder(next.value);
              setPage(0);
            }
          }}
        >
          {SORT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {t(option.labelKey)}
            </option>
          ))}
        </select>

        <div className={style(listStyles.viewToggle)} role="group" aria-label={String(t('view'))}>
          <button
            type="button"
            className={style(listStyles.viewToggleButton(view === 'list', true))}
            aria-pressed={view === 'list'}
            aria-label={String(t('list-view'))}
            onClick={(): void => setView('list')}
          >
            <FormatListBulletedIcon size={18} />
          </button>
          <button
            type="button"
            className={style(listStyles.viewToggleButton(view === 'grid', false))}
            aria-pressed={view === 'grid'}
            aria-label={String(t('grid-view'))}
            onClick={(): void => setView('grid')}
          >
            <ViewGridOutlineIcon size={18} />
          </button>
        </div>

        {hasActiveFilters && (
          <button
            type="button"
            className={style(surfaceStyles.ghostButton)}
            onClick={clearFilters}
            title={String(t('clear-filters'))}
          >
            <FilterRemoveOutlineIcon size={16} />
            {t('clear-filters')}
          </button>
        )}
      </div>
    );
  };

  const renderContent = (): React.JSX.Element => {
    if (isLoading) {
      return (
        <div className={style(listStyles.loadingPad)}>
          <Center>
            <ActivityIndicator
              size={20}
              color={StyleConstants.GREY_COLOR}
            />
          </Center>
        </div>
      );
    }

    if (filteredFacilities.length === 0) {
      return (
        <div className={style(surfaceStyles.emptyState)}>
          {getIconFromFacilityTypeEnum(FacilityTypeEnum.other)}
          <span className={style(surfaceStyles.emptyTitle)}>
            {sortedFacilities.length === 0
              ? t('no-facilities-available')
              : t('no-results-found')}
          </span>
          <span className={style(surfaceStyles.emptyHint)}>
            {sortedFacilities.length === 0
              ? t('no-facilities-available-hint')
              : t('no-facility-results-hint')}
          </span>
        </div>
      );
    }

    const cards = paginatedFacilities.map((item) => (
      <FacilityCard
        key={item.id}
        initialFacility={item}
        allOffices={allOffices}
        variant={view === 'grid' ? 'grid' : 'row'}
        handleChange={shouldUpdateFacility}
        facilityIsValid={facilityIsValid}
        onDelete={(facility): void => {
          shouldDeleteFacility(facility);
        }}
      />
    ));

    if (view === 'grid') {
      return <div className={style(listStyles.cardGrid(isMobile))}>{cards}</div>;
    }

    return <div>{cards}</div>;
  };

  // Read-only view (office detail → Facilities tab): grouped by type with type chips instead of
  // the editable page's dropdown filters and pagination, following the facilities mockup. Every
  // group is shown in full, so nothing is hidden behind a page the reader has to find.
  const renderLinkedFacilities = (): React.JSX.Element => {
    const usedTypes = FACILITY_TYPE_ORDER.filter(
      (type) => sortedFacilities.some((facility) => facility.type === type),
    );

    const groups = usedTypes
      .map((type) => ({
        type,
        items: filteredFacilities.filter((facility) => facility.type === type),
      }))
      .filter((group) => group.items.length > 0);

    const renderBody = (): React.JSX.Element => {
      if (isLoading) {
        return (
          <Center>
            <ActivityIndicator
              size={20}
              color={StyleConstants.GREY_COLOR}
            />
          </Center>
        );
      }

      if (groups.length === 0) {
        const isEmpty = sortedFacilities.length === 0;
        return (
          <div className={style(surfaceStyles.emptyState)}>
            {getIconFromFacilityTypeEnum(FacilityTypeEnum.other)}
            <span className={style(surfaceStyles.emptyTitle)}>
              {isEmpty ? t('no-facilities-available') : t('no-results-found')}
            </span>
            <span className={style(surfaceStyles.emptyHint)}>
              {isEmpty
                ? t('no-linked-facilities-hint')
                : t('no-facility-results-hint')}
            </span>
          </div>
        );
      }

      return (
        <>
          {groups.map(({ type, items }) => {
            const accent = accentForFacilityType(type);
            return (
              <section className={style(surfaceStyles.group)} key={type}>
                <div className={style(surfaceStyles.groupHead)}>
                  <span className={style(linkedStyles.groupIcon(accent))}>
                    {getIconFromFacilityTypeEnum(type)}
                  </span>
                  <span className={style(surfaceStyles.groupTitle)}>
                    {t(facilityTypeLabelKey(type))}
                  </span>
                  <div className={style(surfaceStyles.groupRule)} />
                  <span className={style(surfaceStyles.groupCount)}>
                    {items.length}
                  </span>
                </div>
                <div className={style(linkedStyles.grid(isMobile))}>
                  {items.map((facility) => (
                    // Read-only: the same identity block as the editable list, without the
                    // click target — this tab cannot edit facilities.
                    <article
                      className={style(linkedStyles.facilityCard)}
                      key={facility.id ?? facility.name}
                    >
                      <div className={style(listStyles.gridCardTop)}>
                        <FacilityIdentity facility={facility} />
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            );
          })}
        </>
      );
    };

    return (
      <div className={style(surfaceStyles.card)}>
        <div className={style(surfaceStyles.cardHead(isMobile))}>
          <div>
            <h2 className={style(surfaceStyles.cardTitle)}>
              {t('facilities')}
            </h2>
            <p className={style(surfaceStyles.cardSub)}>
              {t('linked-facilities-card-subtitle')}
            </p>
          </div>
        </div>

        {sortedFacilities.length > 0 && (
          <div className={style(surfaceStyles.toolbar)}>
            <div className={style(surfaceStyles.searchWrap(isMobile))}>
              <span className={style(surfaceStyles.searchIcon)}>
                <MagnifyIcon size={16} />
              </span>
              <input
                type="text"
                className={style(surfaceStyles.searchInput)}
                placeholder={String(t('search-by-name'))}
                aria-label={String(t('search-by-name'))}
                value={searchQuery}
                onChange={handleSearchChange}
              />
            </div>
            <div className={style(linkedStyles.chipRow)}>
              <button
                type="button"
                className={style(
                  linkedStyles.chip(selectedType === ALL_TYPES_VALUE),
                )}
                aria-pressed={selectedType === ALL_TYPES_VALUE}
                onClick={(): void => {
                  setSelectedType(ALL_TYPES_VALUE);
                  setPage(0);
                }}
              >
                {`${t('all-types')} (${sortedFacilities.length})`}
              </button>
              {usedTypes.map((type) => {
                const count = sortedFacilities.filter(
                  (facility) => facility.type === type,
                ).length;
                return (
                  <button
                    type="button"
                    key={type}
                    className={style(
                      linkedStyles.chip(selectedType === String(type)),
                    )}
                    aria-pressed={selectedType === String(type)}
                    onClick={(): void => {
                      setSelectedType(String(type));
                      setPage(0);
                    }}
                  >
                    {`${t(facilityTypeLabelKey(type))} (${count})`}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className={style(linkedStyles.body)}>{renderBody()}</div>
      </div>
    );
  };

  if (isReadonly) {
    return renderLinkedFacilities();
  }

  return (
    <section className={style(surfaceStyles.card)}>
      {renderSectionHead()}
      {renderToolbar()}
      {renderContent()}
      {!isLoading && filteredFacilities.length > 0 && (
        <div className={style(listStyles.sectionFoot)}>
          <ListPager
            page={currentPage}
            pageSize={ITEMS_PER_PAGE}
            total={filteredFacilities.length}
            onChangePage={setPage}
          />
        </div>
      )}
      <CreateFacilityModal
        showModal={showCreateFacilityModal}
        handleClose={handleClose}
        handleCreate={shouldCreateFacility}
        isFacilityValid={facilityIsValid}
      />
    </section>
  );
};

export default OfficeFacilitiesGroup;
